import { useCallback, useEffect, useState } from 'react';

import client from '../api/client';

// --- raw API wrappers ---------------------------------------------------
// Exposed as standalone functions so the UserEditor (a separate route) can
// load/save a single user without pulling in the list hook.

export async function fetchAdminUser(id) {
  const { data } = await client.get(`/user/${id}`);
  return data;
}

export async function fetchUserRoles() {
  const { data } = await client.get('/user/roles');
  return data;
}

export async function updateUser(id, body) {
  // body: any of { fName, lName, phone, active, REF_UserType }
  const { data } = await client.put(`/user/${id}`, body);
  return data;
}

// --- list hook for the admin users page ---------------------------------
// GET /user -> { items, page, limit, total, pages }.

const EMPTY = { items: [], page: 1, limit: 20, total: 0, pages: 1 };

export default function useAdminUsers({ page = 1, limit = 20, q = '', sort = '', active } = {}) {
  const [data, setData] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const buildParams = useCallback(() => {
    const params = { page, limit };
    if (q) params.q = q;
    if (sort) params.sort = sort;
    if (active != null) params.active = active;
    return params;
  }, [page, limit, q, sort, active]);

  const reload = useCallback(async () => {
    const { data: res } = await client.get('/user', { params: buildParams() });
    setData(res && Array.isArray(res.items) ? res : EMPTY);
  }, [buildParams]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    client
      .get('/user', { params: buildParams() })
      .then(({ data: res }) => {
        if (alive) setData(res && Array.isArray(res.items) ? res : EMPTY);
      })
      .catch((err) => {
        if (alive) {
          setData(EMPTY);
          setError(err);
        }
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [buildParams]);

  const update = useCallback(
    async (id, body) => {
      const res = await updateUser(id, body);
      await reload();
      return res;
    },
    [reload]
  );

  return { ...data, loading, error, reload, update };
}
